import React, { useState, useEffect, useMemo, useRef } from 'react';
import { customerPaymentsApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Receipt, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Small INR formatter used only inside this dialog. Kept local to avoid a
// circular import back into CustomerDetail.jsx.
function _fmtINR(n) {
  if (n == null || isNaN(n)) return '\u20B90';
  const sign = n < 0 ? '-' : '';
  const num = Math.round(Math.abs(n) * 100) / 100;
  const [intp, fracp = '00'] = num.toFixed(2).split('.');
  let s = intp;
  if (s.length > 3) {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const groups = [];
    while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
    if (rest) groups.unshift(rest);
    s = groups.join(',') + ',' + last3;
  }
  return `${sign}\u20B9${s}.${fracp}`;
}

/**
 * Receive Payment (FIFO) Dialog — records ONE lump-sum payment received from a
 * customer and auto-allocates it across the customer's outstanding invoices
 * oldest-first. Older invoices are fully paid first; the last touched invoice
 * becomes partially-paid if the amount runs out mid-invoice. Any leftover is
 * highlighted as on-account.
 *
 * Users can switch to a manual split to override the FIFO order.
 *
 * Props:
 *   open, customerId, outstanding, openInvoices, onClose, onRecorded
 */
const ReceivePaymentDialog = ({ open, customerId, outstanding, openInvoices, onClose, onRecorded }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: '',
    payment_mode: 'rtgs',
    reference_number: '',
    date: today,
    notes: '',
    metal_exchange_weight: '',
    metal_exchange_invoice_number: '',
  });
  const [splitMode, setSplitMode] = useState('auto');
  const [manualSplit, setManualSplit] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        amount: '', payment_mode: 'rtgs', reference_number: '', date: today, notes: '',
        metal_exchange_weight: '', metal_exchange_invoice_number: '',
      });
      setSplitMode('auto');
      setManualSplit({});
      setPreview(null);
    }
  }, [open]);

  useEffect(() => {
    if (splitMode !== 'manual') return;
    if (preview?.allocations && Object.keys(manualSplit).length === 0) {
      const seed = {};
      for (const a of preview.allocations) {
        seed[a.dispatch_id] = ((seed[a.dispatch_id] || 0) + (a.amount || 0));
      }
      const asStrings = {};
      Object.entries(seed).forEach(([k, v]) => { asStrings[k] = String(v); });
      setManualSplit(asStrings);
    }
  }, [splitMode, preview, manualSplit]);

  const manualTotal = useMemo(
    () => Object.values(manualSplit).map(v => parseFloat(v || 0) || 0).reduce((s, x) => s + x, 0),
    [manualSplit]
  );

  useEffect(() => {
    if (!open) return;
    const amt = parseFloat(form.amount || '0');
    if (!amt || amt <= 0) { setPreview(null); return; }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const body = {
          amount: amt,
          payment_mode: form.payment_mode,
          reference_number: form.reference_number,
          date: form.date,
          notes: form.notes,
          dry_run: true,
        };
        if (splitMode === 'manual') {
          body.allocations = Object.entries(manualSplit)
            .map(([dispatch_id, v]) => ({ dispatch_id, amount: parseFloat(v || 0) || 0 }))
            .filter(a => a.amount > 0);
        }
        const r = await customerPaymentsApi.receive(customerId, body);
        setPreview(r.data);
      } catch (err) {
        setPreview(null);
        if (splitMode !== 'manual') {
          toast.error(err?.response?.data?.detail || 'Failed to preview allocation');
        }
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [form.amount, open, customerId, splitMode, manualSplit, form.payment_mode, form.reference_number, form.date, form.notes]);

  const commit = async () => {
    const amt = parseFloat(form.amount || '0');
    if (!amt || amt <= 0) { toast.error('Enter an amount'); return; }
    if (form.payment_mode === 'metal_exchange' && !form.metal_exchange_weight) {
      toast.error('Enter the metal-exchange weight (grams 999)');
      return;
    }
    if (splitMode === 'manual') {
      const nonZero = Object.entries(manualSplit).filter(([, v]) => (parseFloat(v || 0) || 0) > 0);
      if (nonZero.length === 0) {
        toast.error('Allocate at least ₹1 to one invoice, or switch to Auto FIFO');
        return;
      }
      if (manualTotal > amt + 0.5) {
        toast.error(`Manual split (₹${manualTotal.toLocaleString('en-IN')}) exceeds the amount received (₹${amt.toLocaleString('en-IN')})`);
        return;
      }
    }
    setSaving(true);
    try {
      const body = {
        amount: amt,
        payment_mode: form.payment_mode,
        reference_number: form.reference_number,
        date: form.date,
        notes: form.notes,
        metal_exchange_weight: parseFloat(form.metal_exchange_weight) || 0,
        metal_exchange_invoice_number: form.metal_exchange_invoice_number || '',
        dry_run: false,
      };
      if (splitMode === 'manual') {
        body.allocations = Object.entries(manualSplit)
          .map(([dispatch_id, v]) => ({ dispatch_id, amount: parseFloat(v || 0) || 0 }))
          .filter(a => a.amount > 0);
      }
      const r = await customerPaymentsApi.receive(customerId, body);
      const summary = r.data;
      const leftMsg = summary.leftover > 0 ? ` Leftover ₹${_fmtINR(summary.leftover).replace('₹', '')} kept on account.` : '';
      toast.success(`Payment of ₹${_fmtINR(summary.total_allocated).replace('₹', '')} allocated across ${summary.invoice_count} invoice(s).${leftMsg}`);
      onRecorded && onRecorded();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  const previewByInvoice = useMemo(() => {
    if (!preview?.allocations) return [];
    const map = new Map();
    for (const a of preview.allocations) {
      const k = a.dispatch_id;
      if (!map.has(k)) {
        map.set(k, {
          invoice_number: a.invoice_number, co_number: a.co_number, invoiced_at: a.invoiced_at,
          order_id: a.order_id, dispatch_id: a.dispatch_id, rows: [], total: 0,
          before: a.invoice_total_pending_before,
        });
      }
      const row = map.get(k);
      row.rows.push({ payment_type: a.payment_type, amount: a.amount });
      row.total += a.amount;
    }
    return Array.from(map.values());
  }, [preview]);

  const totalAllocated = preview?.total_allocated || 0;
  const leftover = preview?.leftover || 0;
  const amountNum = parseFloat(form.amount || '0') || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receive-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-600" /> Payment Received
          </DialogTitle>
          <DialogDescription>
            One payment, auto-allocated across outstanding invoices oldest-first (FIFO).
            Switch to <b>Manual split</b> to edit the per-invoice amounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount received (₹) *</Label>
              <Input
                type="number" min="0" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={`Outstanding: ${_fmtINR(outstanding)}`}
                data-testid="receive-payment-amount" autoFocus
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Outstanding <b>{_fmtINR(outstanding)}</b> across {(openInvoices || []).length} invoice{(openInvoices || []).length === 1 ? '' : 's'}.
              </p>
            </div>
            <div>
              <Label className="text-xs">Payment mode *</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="receive-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rtgs">RTGS</SelectItem>
                  <SelectItem value="neft">NEFT</SelectItem>
                  <SelectItem value="imps">IMPS</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="metal_exchange">Metal exchange</SelectItem>
                  <SelectItem value="sales_return">Sales Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reference / UTR</Label>
              <Input value={form.reference_number}
                onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                placeholder="UTR / cheque no. / txn id" data-testid="receive-payment-reference" />
            </div>
            <div>
              <Label className="text-xs">Payment date *</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="receive-payment-date" />
            </div>
          </div>

          {form.payment_mode === 'metal_exchange' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-500/5 p-3">
              <div>
                <Label className="text-xs">Metal exchange weight (g @ 999)</Label>
                <Input type="number" min="0" step="0.001" value={form.metal_exchange_weight}
                  onChange={(e) => setForm({ ...form, metal_exchange_weight: e.target.value })}
                  data-testid="receive-payment-metal-weight" />
              </div>
              <div>
                <Label className="text-xs">Metal exchange invoice #</Label>
                <Input value={form.metal_exchange_invoice_number}
                  onChange={(e) => setForm({ ...form, metal_exchange_invoice_number: e.target.value })}
                  data-testid="receive-payment-metal-invoice" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional — e.g. 'Payment for July invoices'"
              data-testid="receive-payment-notes" />
          </div>

          <div className="flex items-center justify-between rounded-lg border px-3 py-2 bg-muted/40">
            <div className="text-xs">
              <p className="font-semibold">Allocation split</p>
              <p className="text-muted-foreground">
                {splitMode === 'auto'
                  ? 'FIFO (oldest invoice paid first). Switch to manual to edit per-invoice amounts.'
                  : 'Manual — enter the amount you want applied to each invoice.'}
              </p>
            </div>
            <div className="inline-flex rounded-md border p-0.5 bg-background text-xs">
              <button type="button" onClick={() => setSplitMode('auto')}
                className={cn('px-2.5 py-1 rounded font-medium transition',
                  splitMode === 'auto' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground')}
                data-testid="split-mode-auto-btn">Auto FIFO</button>
              <button type="button" onClick={() => setSplitMode('manual')}
                className={cn('px-2.5 py-1 rounded font-medium transition',
                  splitMode === 'manual' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground')}
                data-testid="split-mode-manual-btn">Manual split</button>
            </div>
          </div>

          {splitMode === 'manual' && (
            <div className="rounded-lg border overflow-hidden" data-testid="manual-split-editor">
              <div className="px-3 py-2 bg-muted/60 border-b flex items-center justify-between text-xs">
                <span className="font-semibold">Enter per-invoice amounts</span>
                <span className={cn('font-mono',
                  Math.abs(manualTotal - amountNum) < 0.5 ? 'text-emerald-700 dark:text-emerald-400' :
                  manualTotal > amountNum ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400')}>
                  {_fmtINR(manualTotal)} / {_fmtINR(amountNum)}
                  {manualTotal > amountNum && ' — exceeds amount'}
                  {manualTotal < amountNum && manualTotal > 0 && ` — ₹${_fmtINR(amountNum - manualTotal).replace('₹', '')} will stay on account`}
                </span>
              </div>
              <div className="divide-y">
                {(openInvoices || []).map((inv) => (
                  <div key={inv.dispatch_id} className="px-3 py-2 flex items-center gap-3" data-testid={`manual-row-${inv.dispatch_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-mono font-semibold">#{inv.invoice_number || inv.co_number}</span>
                        <span className="text-[10px] text-muted-foreground">{(inv.invoice_date || '').slice(0, 10)}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Pending <b>{_fmtINR(inv.balance || 0)}</b> of {_fmtINR(inv.total_amount || 0)}
                      </p>
                    </div>
                    <Input type="number" min="0" step="0.01"
                      className="w-32 h-8 text-right font-mono text-xs"
                      value={manualSplit[inv.dispatch_id] || ''}
                      onChange={(e) => setManualSplit(m => ({ ...m, [inv.dispatch_id]: e.target.value }))}
                      placeholder="0" data-testid={`manual-input-${inv.dispatch_id}`} />
                  </div>
                ))}
                {(openInvoices || []).length === 0 && (
                  <div className="p-4 text-center text-xs text-muted-foreground">No open invoices.</div>
                )}
              </div>
            </div>
          )}

          <div className="rounded-lg border overflow-hidden" data-testid="receive-payment-preview">
            <div className="px-3 py-2 bg-muted/60 border-b flex items-center justify-between text-xs">
              <span className="font-semibold flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3" /> Allocation preview
                {previewLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </span>
              {preview ? (
                <span className="text-muted-foreground">
                  Allocated <b className="text-emerald-700 dark:text-emerald-400">{_fmtINR(totalAllocated)}</b> across {preview.invoice_count} invoice{preview.invoice_count === 1 ? '' : 's'}
                  {leftover > 0 && (
                    <span className="ml-2 text-amber-700 dark:text-amber-400">· <b>Leftover {_fmtINR(leftover)}</b> on account</span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground italic">Type an amount to see the split</span>
              )}
            </div>

            {previewByInvoice.length > 0 && (
              <div className="divide-y">
                {previewByInvoice.map((row) => {
                  const remainingAfter = Math.max(0, (row.before || 0) - row.total);
                  const fullyPaid = remainingAfter < 0.5;
                  return (
                    <div key={row.dispatch_id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`preview-row-${row.dispatch_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-semibold">#{row.invoice_number || row.co_number}</span>
                          {row.invoiced_at && <span className="text-[10px] text-muted-foreground">{row.invoiced_at.slice(0, 10)}</span>}
                          {fullyPaid
                            ? <Badge className="bg-emerald-600 text-white text-[9px] py-0 px-1.5">Fully paid</Badge>
                            : <Badge className="bg-amber-500 text-white text-[9px] py-0 px-1.5">Partially paid</Badge>}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Was ₹{_fmtINR(row.before).replace('₹', '')} pending
                          {' → '}
                          {fullyPaid ? '₹0' : `₹${_fmtINR(remainingAfter).replace('₹', '')} still due`}
                        </p>
                      </div>
                      <div className="text-right tabular-nums font-mono text-sm">
                        <div className="font-semibold text-emerald-700 dark:text-emerald-400">{_fmtINR(row.total)}</div>
                        {row.rows.length > 1 && (
                          <div className="text-[9px] text-muted-foreground">
                            {row.rows.map(r => `${r.payment_type === 'metal' ? 'M' : 'D'} ${_fmtINR(r.amount).replace('₹', '₹')}`).join(' · ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={commit} disabled={saving || !form.amount || parseFloat(form.amount) <= 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="receive-payment-commit-btn">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Recording…</> : <>Record payment</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReceivePaymentDialog;
